'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ALL_COMPONENTS, COMPONENT_GROUPS, type ComponentGroup, type ComponentDef } from '@/lib/archsim-data';
import type { SimulationState } from './ArchitectureCanvasScreen';

interface ComponentPaletteProps {
  onClose: () => void;
  simState: SimulationState;
  isLandscape?: boolean;
}

const GROUP_ORDER: ComponentGroup[] = [
  'Clients', 'Edge', 'Traffic', 'Compute', 'Cache',
  'Database', 'Storage', 'Search', 'Messaging', 'Distributed',
  'Reliability', 'Security', 'Observability', 'Specialized',
];

export default function ComponentPalette({
  onClose,
  simState,
  isLandscape = false,
}: ComponentPaletteProps) {
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<ComponentGroup>>(
    new Set(['Clients', 'Traffic', 'Compute', 'Cache', 'Database'])
  );
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [touchGhost, setTouchGhost] = useState<{ defId: string; x: number; y: number } | null>(null);
  const [mobileCompact, setMobileCompact] = useState(false);
  const touchDragRef = useRef<{ defId: string; pointerId: number } | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return ALL_COMPONENTS;
    const q = search.toLowerCase();
    return ALL_COMPONENTS.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.group.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q)
    );
  }, [search]);

  const grouped = useMemo(() => {
    const map = new Map<ComponentGroup, ComponentDef[]>();
    GROUP_ORDER.forEach(g => map.set(g, []));
    filtered.forEach(c => map.get(c.group)?.push(c));
    return map;
  }, [filtered]);

  const toggleGroup = (group: ComponentGroup) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, defId: string) => {
    e.dataTransfer.setData('application/archsim-component', defId);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggedId(defId);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  // ── Touch drag-and-drop ────────────────────────────────────────────────────
  // HTML5 drag-and-drop does not work with touch input on mobile browsers,
  // so components need a custom touch-driven drag that mirrors the desktop flow.
  const handleTouchStart = (e: React.TouchEvent, defId: string) => {
    if (simState === 'running') return;
    const touch = e.touches[0];
    if (!touch) return;
    touchDragRef.current = { defId, pointerId: touch.identifier };
    setTouchGhost({ defId, x: touch.clientX, y: touch.clientY });
  };

  useEffect(() => {
    if (!touchGhost) return;

    const findTouch = (e: TouchEvent) =>
      Array.from(e.touches).find(t => t.identifier === touchDragRef.current?.pointerId);

    const handleMove = (e: TouchEvent) => {
      const touch = findTouch(e);
      if (!touch || !touchDragRef.current) return;
      e.preventDefault();
      setTouchGhost({ defId: touchDragRef.current.defId, x: touch.clientX, y: touch.clientY });
    };

    const handleEnd = (e: TouchEvent) => {
      const drag = touchDragRef.current;
      touchDragRef.current = null;
      setTouchGhost(null);
      if (!drag) return;
      const touch = Array.from(e.changedTouches).find(t => t.identifier === drag.pointerId);
      if (!touch) return;
      // The palette drawer visually overlaps the canvas on mobile, so hit-testing via
      // elementFromPoint would resolve to the palette itself. Dispatch unconditionally
      // and let the canvas listener decide via a bounding-rect check against its own element.
      window.dispatchEvent(new CustomEvent('archsim-component-drop', {
        detail: { defId: drag.defId, clientX: touch.clientX, clientY: touch.clientY },
      }));
    };

    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
    return () => {
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [touchGhost !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const ghostComponent = touchGhost ? ALL_COMPONENTS.find(c => c.id === touchGhost.defId) : null;
  const touchGhostOverlay = touchGhost && ghostComponent ? (
    <div
      className="fixed z-[999] pointer-events-none flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/50 bg-card shadow-2xl"
      style={{ left: touchGhost.x, top: touchGhost.y, transform: 'translate(-50%, -120%)' }}
    >
      <span className="text-base leading-none">{ghostComponent.icon}</span>
      <span className="text-xs font-medium text-foreground whitespace-nowrap">{ghostComponent.name}</span>
    </div>
  ) : null;

  // Compact icon-only rail — used in landscape mode and as an opt-in mobile mode
  // to reclaim canvas space instead of the full labeled list.
  if (isLandscape || mobileCompact) {
    return (
      <div className={`flex flex-col bg-card/95 border-r border-border/60 overflow-hidden ${isLandscape ? 'game-border' : ''}`} style={{ width: isLandscape ? 52 : 60, height: '100%' }}>
        {/* Header: close + expand-to-list (mobile compact only) */}
        <div className="flex items-center border-b border-border/50 shrink-0">
          {!isLandscape && (
            <button
              onClick={() => setMobileCompact(false)}
              className="flex-1 flex items-center justify-center h-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
              aria-label="Show full component list"
              title="Show full list"
            >
              <PanelLeftOpen size={13} />
            </button>
          )}
          <button
            onClick={onClose}
            className={`flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all ${isLandscape ? 'flex-1 h-7' : 'flex-1 h-8'}`}
            aria-label="Close palette"
          >
            <X size={isLandscape ? 11 : 13} />
          </button>
        </div>

        {/* Scrollable icon list */}
        <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-thin py-1">
          {GROUP_ORDER.map(group => {
            const items = grouped.get(group) ?? [];
            if (items.length === 0) return null;
            const groupColor = COMPONENT_GROUPS[group].color;

            return (
              <div key={`ls-group-${group}`}>
                {/* Group color dot */}
                <div className="flex justify-center py-0.5">
                  <div className="w-1 h-1 rounded-full opacity-40" style={{ backgroundColor: groupColor }} />
                </div>
                {items.map(comp => (
                  <div
                    key={`ls-palette-${comp.id}`}
                    draggable
                    onDragStart={e => handleDragStart(e, comp.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={e => handleTouchStart(e, comp.id)}
                    style={{ touchAction: 'none' }}
                    className={`flex items-center justify-center h-9 mx-1 rounded-md cursor-grab hover:bg-secondary transition-all select-none ${
                      draggedId === comp.id || touchGhost?.defId === comp.id ? 'opacity-50 scale-95' : ''
                    } ${simState === 'running' ? 'opacity-40 cursor-not-allowed' : ''}`}
                    title={`${comp.name} — ${comp.description}`}
                  >
                    <span className="text-base leading-none">{comp.icon}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {touchGhostOverlay}
      </div>
    );
  }

  return (
    <div className="w-44 md:w-60 shrink-0 flex flex-col bg-card border-r border-border overflow-hidden max-h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Components</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground font-mono-nums">{ALL_COMPONENTS.length}</span>
          <button
            onClick={() => setMobileCompact(true)}
            className="md:hidden p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
            aria-label="Switch to compact icon rail"
            title="Compact view"
          >
            <PanelLeftClose size={12} />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
            aria-label="Close palette"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2 bg-secondary rounded-md px-2.5 py-1.5">
          <Search size={12} className="text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Search components..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
              <X size={10} />
            </button>
          )}
        </div>
      </div>

      {/* Groups */}
      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
        {GROUP_ORDER.map(group => {
          const items = grouped.get(group) ?? [];
          if (items.length === 0) return null;
          const groupColor = COMPONENT_GROUPS[group].color;
          const isExpanded = expandedGroups.has(group) || search.trim() !== '';

          return (
            <div key={`group-${group}`} className="border-b border-border/50">
              <button
                onClick={() => toggleGroup(group)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/50 transition-all group"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: groupColor }} />
                  <span className="text-xs font-medium text-foreground">{group}</span>
                  <span className="text-xs text-muted-foreground font-mono-nums">({items.length})</span>
                </div>
                {search.trim() === '' && (
                  isExpanded
                    ? <ChevronDown size={10} className="text-muted-foreground" />
                    : <ChevronRight size={10} className="text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="px-2 pb-2 space-y-0.5">
                  {items.map(comp => (
                    <div
                      key={`palette-${comp.id}`}
                      draggable
                      onDragStart={e => handleDragStart(e, comp.id)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={e => handleTouchStart(e, comp.id)}
                      style={{ touchAction: 'none' }}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-grab hover:bg-secondary transition-all group/item select-none ${
                        draggedId === comp.id || touchGhost?.defId === comp.id ? 'opacity-50' : ''
                      } ${simState === 'running' ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={comp.description}
                    >
                      <span className="text-sm leading-none">{comp.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate leading-tight">{comp.name}</p>
                        <p className="text-xs text-muted-foreground truncate leading-tight" style={{ fontSize: '10px' }}>
                          {comp.defaultLatency > 0 ? `~${comp.defaultLatency}ms` : 'passthrough'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-border">
        <p className="text-xs text-muted-foreground/60 text-center">
          {simState === 'running' ? 'Pause simulation to add components' : 'Drag to canvas to place'}
        </p>
      </div>
      {touchGhostOverlay}
    </div>
  );
}